import React, {useRef, useMemo, useCallback, useState, useEffect} from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import StandardModalContentV2 from '../StandardModalContent/StandardModalContentNew.jsx'

const ValidationModal = ({data, okHandler, closeSelf}) => {
  // data coming in is an api response in the shape:
  // {
  //   confirmation_required: {
  //     override_prop1: ['some override message'],
  //     override_prop2: {
  //        override_subProp1: ['some override message']
  //     }
  //   },
  //   errors: {
  //     prop1: [
  //       'some error about props1',
  //       'another error about prop1'
  //     ],
  //     prop2: {
  //         subProp1: [
  //           'error about subProp1'
  //         ]
  //      }
  //   },
  //   status: 400,
  //   ...
  // }

  const [checkBoxKeys, setCheckBoxKeys] = useState([]);
  const [confirmations, setConfirmations] = useState({});
  const [includesErrors, setIncludesErrors] = useState(false)

  const checkBoxRef = useRef([]);

  const makeRef = (el, key) => checkBoxRef.current[key] = el
  const changeHandlerRef = useRef()

  // recursively analyze each property of the object and update the associated "part" of state.
  const recursiveErrorObjectHandler = useCallback((errorsObj, type = 'error', state = {}) => {
    const newState = _.cloneDeep(state)
    Object.keys(errorsObj).forEach(property => {
      const payload = errorsObj[property]
      newState[property] = recursiveErrorValueHandler(payload, type, {})
    })
    return newState
  },[recursiveErrorValueHandler])

  // recursively analyze each value from the object. Depending ont he type of value
  // we either generate a key, or recursively iterate over sub elements.
  const recursiveErrorValueHandler = useCallback((value, type, state) => {
    if(typeof value == 'string') {
      // this is a property value
      const key = _.uniqueId()
      // convert this to an object containing a key and a value
      setCheckBoxKeys(current => {
        return [...current, { key, value: false }]
      })
      return {
        message: value,
        key
      }
    } else if(Array.isArray(value)) {
      // this is a properties array of values/objects
      return value.map(payloadItem => recursiveErrorValueHandler(payloadItem, type, state))
    } else if(typeof value === 'object') {
      // this is a property value which itself is nested object.
      return recursiveErrorObjectHandler(value, type, state)
    }
  },[checkBoxKeys, recursiveErrorObjectHandler])

  const changeHandler = useCallback((e, key) => {
    const newState = checkBoxKeys.map(
      item => {
        if(item.key === key){
          item.value = e.target.checked
        }
        return item
      }
    )
    setCheckBoxKeys(newState)
  },[checkBoxKeys])

  useEffect(() => {
    // we use the ref because the handler is called from the modal which causes the function to
    // not properly receive updates as state changes. By just giving a modal a ref to the function
    // we can get around that.
    changeHandlerRef.current = changeHandler
  },[])

  const allBoxesChecked = useMemo(() => {
    return checkBoxKeys.every(item => item.value === true)
  },[checkBoxKeys])

  // same as recursiveErrorObjectHandler except rather than updating the state element,
  // it generates UI display elements. Minor changes because recursiveErrorObject and ValueHandlers 
  // converted strings to objects with keys and values so we now need to detect those.
  const recursiveErrorObjectDisplay = useCallback((errorsObj, type, checkBoxKeys) => {
    return <ul className={(type || '')}>
      {
        Object.keys(errorsObj)
        .filter(property => {
          return property !== 'key'    // its an element key, we don't display these
        })
        .map(property => {
          const payload = errorsObj[property]
          if (typeof payload === 'object') {
            if(!payload.key){
              payload.key = _.uniqueId()
            }
            return (
              <li key = {payload.key}>
                <span>{property}</span>
                <ul className={'second_tier ' + (type || '')}>
                  {recursiveErrorValueDisplay(payload, type, checkBoxKeys)}
                </ul>
              </li>
            )
          } else {
            const key = _.uniqueId()
            return <span key={key}>{payload}</span>
          }
        })
      }
    </ul>
  }, [recursiveErrorValueDisplay])

  // save as recursiveErrorValueHandler except rather than updating the state element,
  // it generates UI display elements. 
  const recursiveErrorValueDisplay = useCallback((payload, type, checkBoxKeys) => {
    if(Array.isArray(payload)) {
      // this is a properties array of messages/objects
      return payload.map(payloadItem => recursiveErrorValueDisplay(payloadItem, type, checkBoxKeys))
    } else if(typeof payload === 'object' && payload.key) {
      // this is a string message that we converted into an object when we added a key
      if(type === 'confirmation') {
        const checkBoxKey = checkBoxKeys.find(item => item.key === payload.key) || {}
        const highlight = checkBoxKey.value === true ? '' : 'red-border'
        return (
          <div className={'checkbox-and-message ' + highlight} key={payload.key}>
            <li>
              <input ref = {el => makeRef(el, payload.key)} type="checkbox" value={checkBoxKey.value} onChange={e => changeHandlerRef.current(e, payload.key)}/>{' '}
              {payload.message}
            </li>
          </div>
        )
      } else {
        // this is a property message which itself is nested object.
        return <li key = {payload.key}>{recursiveErrorObjectDisplay(payload, type, checkBoxKeys)}</li>
      }
    } else if(typeof payload === 'object') {
      payload.key = _.uniqueId()
      // this is a property message which itself is nested object.
      return <li key = {payload.key}>{recursiveErrorObjectDisplay(payload, type, checkBoxKeys)}</li>
    } else {
      return <li key={payload.key}>{payload.message}</li>
    }
  },[recursiveErrorObjectDisplay])
  
  const manipulatedErrors = useMemo(() => {
    const {errors = {}} = data
    return recursiveErrorObjectHandler(errors)
  }, [recursiveErrorObjectHandler])

  const manipulatedConfirmations = useMemo(() => {
    const {confirmation_required: confirmationRequired = {}} = data
    return recursiveErrorObjectHandler(confirmationRequired, 'confirmation')
  }, [recursiveErrorObjectDisplay])
  
  useEffect(() => {
    // store the manipulated version of confirmations so its available to the okHandler
    setConfirmations(manipulatedConfirmations)
  },[manipulatedConfirmations])

  useEffect(() => {
    const {errors = {}} = data
    if(Object.keys(errors).length > 0) {
      setIncludesErrors(true)
    }
  },[data.errors])

  const nullHandler = () => null

  const recursivelyGenerateOverrideObject = useCallback((inObject) => {
    const outObject = {}
    Object.keys(inObject).forEach(property => {
      let payload = inObject[property];
      if(Array.isArray(payload) && payload.length == 1 && payload[0].key && payload[0].message) {
        // because of how the api returns error messages, every error property has an array of messages
        // but the way we use overrides in the api, we know overrides only have a single message in them. 
        // Additionally, based on how the recursiveErrorObjectHandler adds keys, we know that if there is
        // a key and a message in this object, then it is in fact an override and we should extract
        // the object out of the array for processing below. 
        payload = payload[0];
      }
      if(payload.key) {
        // find the checkBox with the key associated with this override and if its value is true, 
        // we can set the override value in the out object to 'T'
        const checkBox = checkBoxKeys.find(checkBox => checkBox.key === payload.key)
        if(checkBox && checkBox.value === true) {
          outObject[property] = 'T'
        }
      } else if (typeof payload === 'object') {
        // recursive check
        outObject[property] = recursivelyGenerateOverrideObject(payload)
      }
    })
    return outObject
  }, [checkBoxKeys])

  const submitOverrides = useCallback(() => {
    const overrides = _.cloneDeep(confirmations)
    // need to recursive add each override to the save object as long as the stateKey value = true
    const apiOverrides = recursivelyGenerateOverrideObject(overrides)

    okHandler(apiOverrides)
  },[confirmations, recursivelyGenerateOverrideObject])

  return (
    <StandardModalContentV2
      closeSelf={closeSelf}
      cancelHandler={nullHandler}
      cancelText={includesErrors ? 'OK' : 'Cancel'}
      okHandler={
        includesErrors ? null : submitOverrides
      }
      okText={'OK'}
      okDisabled={!allBoxesChecked}
    >
      <div className = 'validation-modal'>
        {
          Object.keys(manipulatedErrors).length > 0
            ? recursiveErrorObjectDisplay(manipulatedErrors)
            // if there were no errors, then its safe to show the confirmations since they can all be overridden if desired.
            : recursiveErrorObjectDisplay(manipulatedConfirmations, 'confirmation', checkBoxKeys)
        }
      </div>
    </StandardModalContentV2>
  )
}

ValidationModal.propTypes = {
  // Primary modal message
  message: PropTypes.string,
  // Xtract api validation response object
  data: PropTypes.object.isRequired,
  // Optional function to execute when OK button is clicked
  okHandler: PropTypes.func
}

export default ValidationModal
